/**
 * PrimitiveConstellationPage — Admin page for exploring FERP primitive
 * field structures via the concentric-ring constellation visualization.
 *
 * Combines: primitive card selector (phase-grouped), context selector,
 * Cytoscape graph, category ring cards, detail panel, and AI-powered
 * field suggestions via Bedrock Claude.
 *
 * Route: /architect/field-constellation
 */

import React, { useState, useCallback } from 'react';
import {
  ActionIcon,
  Badge,
  Box,
  Card,
  Container,
  Collapse,
  Divider,
  Grid,
  Group,
  Modal,
  Paper,
  ScrollArea,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconArrowsMaximize,
  IconChevronDown,
  IconChevronRight,
  IconCircleFilled,
  IconLock,
  IconShieldCheck,
  IconStack2,
} from '@tabler/icons-react';

import {
  PrimitiveFieldConstellationGraph,
  FieldNodeDetailPanel,
  ConstellationContextSelector,
  usePrimitiveConstellation,
  AISuggestPanel,
} from '@ferp/workflow-forms/scope';

import type { FieldConstellationNode } from '@ferp/workflow-forms/scope';
import { useSuggestFieldsMutation } from '@/generated/graphql';

// ---------------------------------------------------------------------------
// Phase definitions with color themes
// ---------------------------------------------------------------------------

interface PhaseTheme {
  bg: string;
  bgHover: string;
  bgSelected: string;
  border: string;
  borderHover: string;
  text: string;
  accent: string;
  icon: React.ReactNode;
}

const PHASE_THEMES: { [key: string]: PhaseTheme } & { DESIGN: PhaseTheme } = {
  DESIGN: {
    bg: '#f3f0ff',
    bgHover: '#e5dbff',
    bgSelected: '#d0bfff',
    border: '#7048e8',
    borderHover: '#845ef7',
    text: '#5f3dc4',
    accent: 'violet',
    icon: <IconStack2 size={14} />,
  },
  CONFIGURE: {
    bg: '#fff4e6',
    bgHover: '#ffe8cc',
    bgSelected: '#ffd8a8',
    border: '#fd7e14',
    borderHover: '#ff922b',
    text: '#e8590c',
    accent: 'orange',
    icon: <IconShieldCheck size={14} />,
  },
  EXECUTE: {
    bg: '#e6fcf5',
    bgHover: '#c3fae8',
    bgSelected: '#96f2d7',
    border: '#20c997',
    borderHover: '#38d9a9',
    text: '#0ca678',
    accent: 'teal',
    icon: <IconCircleFilled size={14} />,
  },
  MONITOR: {
    bg: '#e7f5ff',
    bgHover: '#d0ebff',
    bgSelected: '#a5d8ff',
    border: '#339af0',
    borderHover: '#4dabf7',
    text: '#1971c2',
    accent: 'blue',
    icon: <IconCircleFilled size={14} />,
  },
  OPTIMIZE: {
    bg: '#fff0f6',
    bgHover: '#ffdeeb',
    bgSelected: '#fcc2d7',
    border: '#e64980',
    borderHover: '#f06595',
    text: '#c2255c',
    accent: 'pink',
    icon: <IconCircleFilled size={14} />,
  },
};

// ---------------------------------------------------------------------------
// All 25 FERP primitives grouped by lifecycle phase.
// ---------------------------------------------------------------------------

const PRIMITIVE_PHASES: Array<{
  phase: string;
  primitives: string[];
}> = [
  { phase: 'DESIGN', primitives: ['Workflow', 'Template', 'Dependency', 'Policy', 'Role'] },
  { phase: 'CONFIGURE', primitives: ['Team', 'Actor', 'Validation', 'Contract', 'Constraint'] },
  { phase: 'EXECUTE', primitives: ['Instance', 'Task', 'Approval', 'Document', 'Queue'] },
  { phase: 'MONITOR', primitives: ['Event', 'Notification', 'Trace', 'Checkpoint', 'Transaction'] },
  { phase: 'OPTIMIZE', primitives: ['Audit', 'Analytics', 'Schedule', 'Resource', 'Integration'] },
];

const DEFAULT_THEME: PhaseTheme = PHASE_THEMES.DESIGN;

// ---------------------------------------------------------------------------
// PrimitiveCard — Selectable card for a single primitive.
//
// All visual state driven by React (isSelected + hovered), zero imperative
// DOM manipulation — avoids stale-closure and shorthand/longhand conflicts.
// ---------------------------------------------------------------------------

function PrimitiveCard({
  name,
  phase,
  isSelected,
  onClick,
}: {
  name: string;
  phase: string;
  isSelected: boolean;
  onClick: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const theme = PHASE_THEMES[phase] ?? DEFAULT_THEME;

  // Compute styles declaratively from state
  const background = isSelected
    ? theme.bgSelected
    : hovered
      ? theme.bgHover
      : theme.bg;

  const borderColor = isSelected
    ? theme.border
    : hovered
      ? theme.borderHover
      : 'transparent';

  const shadow = isSelected
    ? `0 0 0 2px ${theme.border}40, 0 2px 8px ${theme.border}25`
    : hovered
      ? `0 2px 8px ${theme.border}20`
      : 'none';

  return (
    <UnstyledButton
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'block',
        borderRadius: 8,
      }}
    >
      <Box
        style={{
          position: 'relative',
          padding: '8px 10px 8px 14px',
          borderRadius: 8,
          background,
          border: `2px solid ${borderColor}`,
          boxShadow: shadow,
          transform: isSelected ? 'scale(1.03)' : hovered ? 'translateY(-1px)' : 'none',
          transition: 'all 0.15s ease',
          overflow: 'hidden',
          cursor: 'pointer',
        }}
      >
        {/* Phase accent bar */}
        <Box
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 4,
            background: theme.border,
            opacity: isSelected ? 1 : hovered ? 0.7 : 0.3,
            transition: 'opacity 0.15s ease',
          }}
        />
        <Text
          size="sm"
          fw={isSelected ? 700 : 500}
          c={isSelected ? theme.text : undefined}
          style={{ userSelect: 'none' }}
        >
          {name}
        </Text>
      </Box>
    </UnstyledButton>
  );
}

// ---------------------------------------------------------------------------
// FieldRow — A single field inside an expanded CategoryRingCard.
//
// Manages its own hover state so parent doesn't need per-field tracking.
// ---------------------------------------------------------------------------

function FieldRow({
  field,
  color,
  isSelected,
  isDimmed,
  onSelect,
}: {
  field: FieldConstellationNode;
  color: string;
  isSelected: boolean;
  isDimmed: boolean;
  onSelect: () => void;
}) {
  const [hovered, setHovered] = useState(false);

  const background = isSelected
    ? `${color}22`
    : hovered
      ? `${color}0D`
      : 'transparent';

  const borderColor = isSelected ? color : 'transparent';

  return (
    <UnstyledButton
      onClick={onSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ display: 'block', width: '100%' }}
    >
      <Group
        gap="xs"
        px="xs"
        py={6}
        wrap="nowrap"
        style={{
          borderRadius: 4,
          background,
          border: `1px solid ${borderColor}`,
          opacity: isDimmed ? 0.4 : 1,
          transition: 'all 0.1s ease',
        }}
      >
        <Text
          size="xs"
          fw={isSelected ? 600 : 400}
          style={{ flex: 1 }}
          truncate
        >
          {field.name}
        </Text>
        <Group gap={4} wrap="nowrap">
          {field.required && (
            <Badge size="xs" color="red" variant="dot">
              req
            </Badge>
          )}
          {field.promoted && (
            <Badge size="xs" color="yellow" variant="filled" c="dark">
              +
            </Badge>
          )}
          {field.piiClassification &&
            field.piiClassification !== 'none' && (
              <Tooltip label={`PII: ${field.piiClassification}`}>
                <ThemeIcon size={16} color="orange" variant="light" radius="xl">
                  <IconLock size={10} />
                </ThemeIcon>
              </Tooltip>
            )}
          <Badge size="xs" variant="light" color="gray">
            {field.fieldType ?? '?'}
          </Badge>
        </Group>
      </Group>
    </UnstyledButton>
  );
}

// ---------------------------------------------------------------------------
// CategoryRingCard — Expandable card for a field category ring.
//
// Header click toggles expand/collapse. Individual fields are selectable.
// ---------------------------------------------------------------------------

function CategoryRingCard({
  label,
  color,
  fieldCount,
  isExpanded,
  isHighlighted,
  fields,
  selectedFieldName,
  onToggle,
  onFieldSelect,
  contextActive,
}: {
  label: string;
  color: string;
  fieldCount: number;
  isExpanded: boolean;
  isHighlighted: boolean;
  fields: FieldConstellationNode[];
  selectedFieldName: string | null;
  onToggle: () => void;
  onFieldSelect: (fieldName: string | null) => void;
  contextActive: boolean;
}) {
  const [headerHovered, setHeaderHovered] = useState(false);

  const headerBg = isExpanded
    ? `${color}14`
    : headerHovered
      ? `${color}0A`
      : 'transparent';

  return (
    <Card
      withBorder
      padding={0}
      radius="md"
      style={{
        borderColor: isHighlighted ? '#ffd43b' : undefined,
        borderWidth: isHighlighted ? 2 : 1,
        overflow: 'hidden',
      }}
    >
      {/* Header — click to expand/collapse */}
      <UnstyledButton
        onClick={onToggle}
        onMouseEnter={() => setHeaderHovered(true)}
        onMouseLeave={() => setHeaderHovered(false)}
        w="100%"
        style={{ display: 'block' }}
      >
        <Group
          gap="sm"
          p="sm"
          wrap="nowrap"
          style={{
            background: headerBg,
            transition: 'background 0.15s ease',
          }}
        >
          {/* Color dot */}
          <Box
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: color,
              flexShrink: 0,
            }}
          />
          <Text size="sm" fw={600} style={{ flex: 1 }}>
            {label}
          </Text>
          <Badge
            size="sm"
            variant="light"
            color={isHighlighted ? 'yellow' : 'gray'}
          >
            {fieldCount}
          </Badge>
          {isExpanded ? (
            <IconChevronDown size={16} style={{ color: '#868e96' }} />
          ) : (
            <IconChevronRight size={16} style={{ color: '#868e96' }} />
          )}
        </Group>
      </UnstyledButton>

      {/* Expanded field list */}
      <Collapse in={isExpanded}>
        <Divider />
        <Stack gap={0} p="xs">
          {fields.map((field) => {
            const fieldName = field.nodeId.replace('field-', '');
            const isFieldSelected = selectedFieldName === fieldName;
            const isDimmed = contextActive && !field.promoted;

            return (
              <FieldRow
                key={field.nodeId}
                field={field}
                color={color}
                isSelected={isFieldSelected}
                isDimmed={isDimmed}
                onSelect={() => onFieldSelect(isFieldSelected ? null : fieldName)}
              />
            );
          })}
        </Stack>
      </Collapse>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function PrimitiveConstellationPage() {
  const [primitiveType, setPrimitiveType] = useState<string>('Task');
  const [selectedFieldName, setSelectedFieldName] = useState<string | null>(null);
  const [graphExpanded, { open: openGraphModal, close: closeGraphModal }] = useDisclosure(false);

  const {
    constellation,
    isLoading,
    contextProfiles,
    activeContextId,
    setContextId,
    expandedCategories,
    toggleCategory,
  } = usePrimitiveConstellation({ primitiveType });

  const [suggestFields] = useSuggestFieldsMutation();

  // Find the selected field node for the detail panel
  const selectedField: FieldConstellationNode | null =
    constellation?.fieldNodes.find(
      (f) => f.nodeId === `field-${selectedFieldName}`,
    ) ?? null;

  // Which phase the current primitive belongs to
  const currentPhase = PRIMITIVE_PHASES.find((p) =>
    p.primitives.includes(primitiveType),
  )?.phase ?? 'DESIGN';

  const currentPhaseTheme = PHASE_THEMES[currentPhase] ?? DEFAULT_THEME;

  const handleFieldSelect = useCallback((fieldName: string | null) => {
    setSelectedFieldName(fieldName);
  }, []);

  const handleDetailClose = useCallback(() => {
    setSelectedFieldName(null);
  }, []);

  const handleCategoryToggle = useCallback(
    (ringId: string) => {
      toggleCategory(ringId);
    },
    [toggleCategory],
  );

  const handleSuggest = useCallback(
    async (input: {
      primitiveType: string;
      contextId?: string | null;
      userGoal: string;
      vertical?: string | null;
    }) => {
      const { data } = await suggestFields({
        variables: {
          input: {
            primitiveType: input.primitiveType,
            contextId: input.contextId,
            userGoal: input.userGoal,
            vertical: input.vertical,
          },
        },
      });
      if (!data?.suggestFields) {
        throw new Error('No response from suggestFields mutation');
      }
      return {
        suggestions: data.suggestFields.suggestions.map((s) => ({
          fieldName: s.fieldName,
          ringId: s.ringId,
          reasoning: s.reasoning,
          relevance: s.relevance,
          critical: s.critical,
        })),
        provider: data.suggestFields.provider,
        totalFieldsConsidered: data.suggestFields.totalFieldsConsidered,
      };
    },
    [suggestFields],
  );

  const handleHighlightField = useCallback((fieldName: string) => {
    setSelectedFieldName(fieldName);
  }, []);

  // Group fields by ring for category cards
  const fieldsByRing = new Map<string, FieldConstellationNode[]>();
  if (constellation) {
    for (const field of constellation.fieldNodes) {
      const ringId = field.ringId ?? '';
      const existing = fieldsByRing.get(ringId);
      if (existing) {
        existing.push(field);
      } else {
        fieldsByRing.set(ringId, [field]);
      }
    }
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        {/* ── Header ──────────────────────────────────────────────── */}
        <Group justify="space-between" align="flex-start">
          <Stack gap={4}>
            <Title order={3}>Field Constellation</Title>
            <Text size="sm" c="dimmed">
              Select a primitive to explore its field structure. Click categories to browse fields.
            </Text>
          </Stack>
          <Group gap="xs">
            {constellation && (
              <>
                <Badge variant="filled" color="violet">
                  {constellation.totalFieldCount} fields
                </Badge>
                <Badge variant="outline" color="gray">
                  {constellation.rings.length} categories
                </Badge>
                {constellation.activeContextId && (
                  <Badge variant="filled" color="yellow" c="dark">
                    {constellation.promotedFieldCount} promoted
                  </Badge>
                )}
              </>
            )}
          </Group>
        </Group>

        {/* ── Primitive Card Selector (phase-grouped) ─────────────── */}
        <Paper withBorder p="md" radius="md">
          <Stack gap="md">
            {PRIMITIVE_PHASES.map(({ phase, primitives }) => {
              const phaseTheme = PHASE_THEMES[phase] ?? DEFAULT_THEME;
              return (
                <Box key={phase}>
                  <Group gap="xs" mb={8}>
                    <Badge
                      variant="light"
                      color={phaseTheme.accent}
                      size="sm"
                      leftSection={phaseTheme.icon}
                    >
                      {phase}
                    </Badge>
                  </Group>
                  <SimpleGrid cols={{ base: 2, sm: 3, md: 5 }} spacing="xs">
                    {primitives.map((name) => (
                      <PrimitiveCard
                        key={name}
                        name={name}
                        phase={phase}
                        isSelected={primitiveType === name}
                        onClick={() => {
                          setPrimitiveType(name);
                          setSelectedFieldName(null);
                        }}
                      />
                    ))}
                  </SimpleGrid>
                </Box>
              );
            })}
          </Stack>
        </Paper>

        {/* ── Context Profile Selector ────────────────────────────── */}
        <ConstellationContextSelector
          profiles={contextProfiles}
          activeContextId={activeContextId}
          onChange={setContextId}
        />

        {/* ── Main Content: Graph + Category Cards + AI Panel ──── */}
        <Grid gutter="md">
          {/* Graph area */}
          <Grid.Col span={{ base: 12, md: 7 }}>
            <Paper withBorder p="sm" radius="md" style={{ minHeight: 500 }}>
              <Group justify="space-between" mb="xs">
                <Group gap="xs">
                  <Badge variant="filled" color={currentPhaseTheme.accent}>
                    {primitiveType}
                  </Badge>
                </Group>
                <Tooltip label="Expand to fullscreen">
                  <ActionIcon variant="light" color="gray" onClick={openGraphModal}>
                    <IconArrowsMaximize size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
              {isLoading ? (
                <Text c="dimmed" ta="center" py="xl">
                  Loading constellation...
                </Text>
              ) : !constellation ? (
                <Text c="dimmed" ta="center" py="xl">
                  No constellation data available. Backend query may not be connected.
                </Text>
              ) : (
                <PrimitiveFieldConstellationGraph
                  constellation={constellation}
                  height={500}
                  onFieldSelect={handleFieldSelect}
                  onCategoryToggle={handleCategoryToggle}
                  expandedCategories={expandedCategories}
                  contextActive={activeContextId != null}
                />
              )}
            </Paper>
          </Grid.Col>

          {/* Category cards sidebar */}
          <Grid.Col span={{ base: 12, md: 5 }}>
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm" fw={600}>
                  Field Categories
                </Text>
                <Text size="xs" c="dimmed">
                  Click to expand
                </Text>
              </Group>

              <ScrollArea.Autosize mah={560} offsetScrollbars>
                <Stack gap="xs">
                  {constellation?.rings.map((ring) => (
                    <CategoryRingCard
                      key={ring.ringId}
                      label={ring.label}
                      color={ring.color}
                      fieldCount={ring.fieldCount}
                      isExpanded={expandedCategories.has(ring.ringId)}
                      isHighlighted={ring.highlighted}
                      fields={fieldsByRing.get(ring.ringId) ?? []}
                      selectedFieldName={selectedFieldName}
                      onToggle={() => handleCategoryToggle(ring.ringId)}
                      onFieldSelect={handleFieldSelect}
                      contextActive={activeContextId != null}
                    />
                  ))}
                </Stack>
              </ScrollArea.Autosize>

              {/* AI Suggest button (floats at bottom of sidebar) */}
              <AISuggestPanel
                primitiveType={primitiveType}
                contextId={activeContextId}
                onSuggest={handleSuggest}
                onHighlightField={handleHighlightField}
              />
            </Stack>
          </Grid.Col>
        </Grid>

        {/* ── Field Detail Panel (inline, below graph) ────────── */}
        {selectedField && (
          <FieldNodeDetailPanel
            selectedField={selectedField}
            onClose={handleDetailClose}
          />
        )}
      </Stack>

      {/* ── Fullscreen Modal ──────────────────────────────────── */}
      <Modal
        opened={graphExpanded}
        onClose={closeGraphModal}
        fullScreen
        title={
          <Group gap="xs">
            <Title order={4}>Field Constellation</Title>
            <Badge variant="filled" color={currentPhaseTheme.accent}>
              {primitiveType}
            </Badge>
            {constellation && (
              <Badge variant="light" color="gray">
                {constellation.totalFieldCount} fields
              </Badge>
            )}
          </Group>
        }
      >
        {constellation && (
          <PrimitiveFieldConstellationGraph
            constellation={constellation}
            height={window.innerHeight - 120}
            onFieldSelect={handleFieldSelect}
            onCategoryToggle={handleCategoryToggle}
            expandedCategories={expandedCategories}
            contextActive={activeContextId != null}
          />
        )}
      </Modal>
    </Container>
  );
}
